/**
 * OrphanClientOrders — Phase 101ea.4 (Feb 2026)
 *
 * Page /commandes-orphelines pour rattacher rétroactivement les CC qui
 * n'ont jamais eu de `reseller_id`. Utile pour Alain qui a un historique
 * Chermignon (1 CC/semaine × 1 an) sans revendeur assigné.
 *
 * Features :
 *   - Liste des CC orphelines (non annulées)
 *   - Suggestion auto via linked_distributor_id du revendeur
 *   - Sélection multiple + rattachement en 1 clic
 *   - Bouton "Accepter toutes les suggestions"
 *   - Cascade optionnelle aux OFs et lots
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, CheckCircle2, Filter, Link2, Loader2, RefreshCw, Sparkles, Users,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const CHF = (v) => (Number(v || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");

export default function OrphanClientOrders() {
  const [items, setItems] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState({});  // co_id → reseller_id choisi
  const [checked, setChecked] = useState({});      // co_id → bool
  const [filter, setFilter] = useState("");
  const [cascade, setCascade] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orphansRes, resellersRes] = await Promise.all([
        api.get("/client-orders/without-reseller?limit=1000"),
        api.get("/resellers"),
      ]);
      const orphans = orphansRes.data;
      setItems(orphans.items || []);
      // Pré-remplir la selection avec les suggestions
      const initialSel = {};
      for (const it of orphans.items || []) {
        if (it.suggested_reseller_id) {
          initialSel[it.id] = it.suggested_reseller_id;
        }
      }
      setSelection(initialSel);
      // Filtrer revendeurs actifs
      const activeResellers = (resellersRes.data || [])
        .filter((r) => !r.archived)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setResellers(activeResellers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.toLowerCase();
    return items.filter(
      (it) =>
        (it.reference || "").toLowerCase().includes(q) ||
        (it.distributor_name || "").toLowerCase().includes(q) ||
        (it.suggested_reseller_name || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  const withSuggestion = items.filter((it) => it.suggested_reseller_id).length;
  const withoutSuggestion = items.length - withSuggestion;
  const selectedCount = Object.values(checked).filter(Boolean).length;
  const totalAmountSelected = filteredItems
    .filter((it) => checked[it.id])
    .reduce((s, it) => s + it.total, 0);

  const toggleAll = (val) => {
    const next = {};
    if (val) {
      filteredItems.forEach((it) => {
        // Coche uniquement si un revendeur est choisi
        if (selection[it.id]) next[it.id] = true;
      });
    }
    setChecked(next);
  };

  const acceptAllSuggestions = () => {
    const next = { ...selection };
    const nextChecked = { ...checked };
    for (const it of items) {
      if (it.suggested_reseller_id) {
        next[it.id] = it.suggested_reseller_id;
        nextChecked[it.id] = true;
      }
    }
    setSelection(next);
    setChecked(nextChecked);
    toast.success(`${withSuggestion} suggestions acceptées`);
  };

  const applyReseller = (co_id, reseller_id) => {
    setSelection((s) => ({ ...s, [co_id]: reseller_id }));
    if (reseller_id) {
      setChecked((c) => ({ ...c, [co_id]: true }));
    }
  };

  const bulkApply = async () => {
    const toApply = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([co_id]) => ({ co_id, reseller_id: selection[co_id] }))
      .filter((x) => x.reseller_id);
    if (!toApply.length) {
      toast.error("Coche au moins une commande avec un revendeur choisi");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/client-orders/attach-reseller-batch`, {
        items: toApply,
        cascade_to_ofs_and_lots: cascade,
      });
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.success}/${data.processed} rattachements OK — ${data.errors.length} erreur(s)`);
        console.warn("Erreurs batch:", data.errors);
      } else {
        toast.success(`${data.success} commande(s) rattachée(s) avec succès`);
      }
      setChecked({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rattachement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="orphan-co-page">
      <div className="flex items-center gap-3">
        <Link to="/statistiques" className="text-[#575A57] hover:text-[#1F4E3D]" data-testid="orphan-co-back">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] flex items-center gap-2">
            <Link2 className="w-6 h-6 text-[#8C6B2F]" />
            Commandes clients à rattacher
          </h1>
          <p className="text-sm text-[#575A57]">
            Rattachement rétroactif des CC créées sans revendeur (livraisons directes,
            historique pré-Phase 66, ou clients revendeur non identifiés à l'époque).
            Impact : ces CC réintègrent le CA de leur revendeur dans /statistiques Revendeurs B2B.
          </p>
        </div>
      </div>

      {/* KPI + actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">CC orphelines</div>
            <div className="text-2xl font-semibold text-[#1A1C1A]">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Avec suggestion auto</div>
            <div className="text-2xl font-semibold text-[#1F4E3D]">{withSuggestion}</div>
            <div className="text-[10px] text-[#575A57] mt-1">Via distributeur lié à un revendeur</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Sans suggestion</div>
            <div className="text-2xl font-semibold text-[#C05C47]">{withoutSuggestion}</div>
            <div className="text-[10px] text-[#575A57] mt-1">À rattacher manuellement</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Sélection</div>
            <div className="text-2xl font-semibold text-[#8C6B2F]">{selectedCount}</div>
            <div className="text-[10px] text-[#575A57] mt-1 font-mono">{CHF(totalAmountSelected)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions batch */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            onClick={acceptAllSuggestions}
            disabled={loading || withSuggestion === 0}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="orphan-co-accept-all"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Accepter les {withSuggestion} suggestions
          </Button>

          <div className="flex-1" />

          <label className="flex items-center gap-2 text-xs text-[#575A57] cursor-pointer">
            <Checkbox checked={cascade} onCheckedChange={setCascade} data-testid="orphan-co-cascade" />
            Cascade aux OFs et lots liés (recommandé)
          </label>

          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="orphan-co-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Rafraîchir
          </Button>

          <Button
            size="sm"
            onClick={bulkApply}
            disabled={saving || selectedCount === 0}
            className="bg-[#8C6B2F] hover:bg-[#725520] text-white"
            data-testid="orphan-co-bulk-apply"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Rattacher {selectedCount} CC
          </Button>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2 items-center">
            <Filter className="w-4 h-4 text-[#575A57]" />
            <Input
              placeholder="Filtrer par référence, distributeur, revendeur suggéré…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-md"
              data-testid="orphan-co-filter"
            />
            <div className="text-xs text-[#575A57]">{filteredItems.length} / {items.length}</div>
          </div>

          <div className="border border-[#E5E5E0] rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                <tr>
                  <th className="text-left px-2 py-2 w-8">
                    <Checkbox
                      checked={selectedCount > 0 && selectedCount === filteredItems.filter((it) => selection[it.id]).length}
                      onCheckedChange={toggleAll}
                      data-testid="orphan-co-check-all"
                    />
                  </th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Référence</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Distributeur / client</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Statut</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase text-[#575A57]">Total</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Rattacher à</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => (
                  <tr key={it.id} className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`orphan-co-row-${it.id}`}>
                    <td className="px-2 py-2">
                      <Checkbox
                        checked={!!checked[it.id]}
                        onCheckedChange={(v) => setChecked((c) => ({ ...c, [it.id]: !!v }))}
                        disabled={!selection[it.id]}
                        data-testid={`orphan-co-check-${it.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{it.reference || "—"}</td>
                    <td className="px-3 py-2 text-xs text-[#575A57]">
                      {it.order_date ? new Date(it.order_date).toLocaleDateString("fr-CH") : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{it.distributor_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <Badge className="bg-[#F0F5FB] text-[#2F5A8C] border-[#C3D1E8] text-[10px]">
                        {it.status || "?"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{CHF(it.total)}</td>
                    <td className="px-3 py-2">
                      <Select
                        value={selection[it.id] || ""}
                        onValueChange={(v) => applyReseller(it.id, v)}
                      >
                        <SelectTrigger className="text-xs h-8 w-full max-w-xs" data-testid={`orphan-co-select-${it.id}`}>
                          <SelectValue placeholder="Choisir un revendeur…" />
                        </SelectTrigger>
                        <SelectContent>
                          {resellers.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.name}
                              {it.suggested_reseller_id === r.id && (
                                <span className="ml-1 text-[#B68A3E]">✨</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {it.suggested_reseller_id && (
                        <div className="text-[9px] text-[#8C6B2F] mt-0.5">
                          ✨ Suggestion : {it.suggested_reseller_name}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-[#575A57] text-sm">
                      {items.length === 0 ? "Aucune commande orpheline — tout est rattaché ✅" : "Aucune commande ne correspond au filtre"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
